import React from "react";
import type { IconNames } from "../Icons/LucideIcons";
import { Icon } from "../Icons/LucideIcons";
import { Body } from "../Typography";
import {
  ButtonContentContainer,
  ButtonContentWrapper,
  LoadingOverlayContainer,
  LoadingWrapper,
  StyledButton,
} from "./__styles__/Button";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "destructive"
  | "outline"
  | "outlineInverted";

export type ButtonSize = "small" | "medium" | "large";

// this [somewhat] discriminated union prevents incorrect usage of the button
// props; specifically, it requires that a button *either* have a title *or* a
// singular icon; when a title is provided, it optionally allows start and end
// icons
export type ContentProps =
  | {
      icon?: never;
      iconStart?: IconNames;
      iconEnd?: IconNames;
      title: string;
      "aria-label"?: string;
    }
  | {
      icon: IconNames;
      iconStart?: never;
      iconEnd?: never;
      title?: never;
      "aria-label": string;
    };

export type ButtonProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  disabled?: boolean;
  onClick?: (e: any) => void;
  testId?: string;
} & ContentProps;

export function Button({
  variant = "primary",
  size = "medium",
  title,
  iconStart,
  iconEnd,
  icon,
  loading,
  disabled,
  onClick,
  testId,
  "aria-label": ariaLabel,
}: ButtonProps) {
  const resolvedAriaLabel = ariaLabel ?? title ?? "";
  const contentColor =
    ["primary", "destructive", "outlineInverted"].includes(variant) ?
      "contentPrimaryInverted"
    : "contentPrimary";

  return (
    <StyledButton
      variant={variant}
      size={size}
      iconOnly={!!icon}
      hasIconStart={!!iconStart}
      hasIconEnd={!!iconEnd}
      isDisabled={disabled || loading}
      aria-label={resolvedAriaLabel}
      onPress={onClick}
      data-testid={testId}
    >
      <>
        <ButtonContentWrapper loading={loading}>
          {icon ?
            <ButtonContentContainer>
              <Icon
                iconName={icon}
                size={16}
                color={contentColor}
                aria-hidden={true}
              />
            </ButtonContentContainer>
          : <ButtonContentContainer>
              {iconStart ?
                <Icon
                  iconName={iconStart}
                  size={16}
                  color={contentColor}
                  aria-hidden={true}
                />
              : null}
              {title ?
                <Body
                  type="regular"
                  size={size === "small" ? "default" : "large"}
                  color={contentColor}
                >
                  {title}
                </Body>
              : null}
              {iconEnd ?
                <Icon
                  iconName={iconEnd}
                  size={16}
                  color={contentColor}
                  aria-hidden={true}
                />
              : null}
            </ButtonContentContainer>
          }
        </ButtonContentWrapper>
        {loading ?
          <LoadingOverlayContainer>
            <LoadingWrapper>
              <Icon
                iconName="loading"
                size={16}
                color={contentColor}
                aria-label="Loading"
              />
            </LoadingWrapper>
          </LoadingOverlayContainer>
        : null}
      </>
    </StyledButton>
  );
}
